// =============================================================================
// components/ux/NightLightControls.tsx
// Floating Night Light control panel — Brightness, Glow, Contrast, Laser.
// Always-on safety rule: text never drops below WCAG AA floor.
// =============================================================================

import { useState, type JSX } from 'react';
import { Moon, Sun, Shield, RotateCcw, X, Info } from 'lucide-react';
import { useNightLightStore } from '../../stores/nightLight.store.js';

interface SliderProps {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  accentColor: string;
  onChange: (v: number) => void;
  safeFloor?: number;
  description?: string;
}

function NightSlider({ label, value, min = 0, max = 1, step = 0.01, accentColor, onChange, safeFloor, description }: SliderProps): JSX.Element {
  const pct = ((value - min) / (max - min)) * 100;
  const atFloor = safeFloor !== undefined && value <= safeFloor + 0.02;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(220,232,248,0.85)', letterSpacing: '0.06em' }}>
          {label}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {atFloor && safeFloor !== undefined && (
            <span style={{ fontSize: 9, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 3, fontWeight: 700 }}>
              <Shield size={9} /> FLOOR
            </span>
          )}
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: accentColor, fontWeight: 700 }}>
            {Math.round(value * 100)}%
          </span>
        </div>
      </div>

      {/* Custom slider track */}
      <div style={{ position: 'relative', height: 24, display: 'flex', alignItems: 'center' }}>
        {/* Track background */}
        <div style={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 2, background: 'rgba(30,50,80,0.6)' }} />
        {/* Filled portion */}
        <div style={{
          position: 'absolute', left: 0, width: `${pct}%`, height: 4, borderRadius: 2,
          background: `linear-gradient(90deg, ${accentColor}44, ${accentColor})`,
          boxShadow: `0 0 8px ${accentColor}50`,
        }} />
        {/* Safety floor marker */}
        {safeFloor !== undefined && (
          <div style={{
            position: 'absolute',
            left: `${((safeFloor - min) / (max - min)) * 100}%`,
            width: 2, height: 10, background: '#fbbf24',
            borderRadius: 1, top: '50%', transform: 'translateY(-50%)',
            boxShadow: '0 0 4px rgba(251,191,36,0.5)',
          }} />
        )}
        <input
          type="range"
          min={min} max={max} step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          style={{
            position: 'absolute', inset: 0, width: '100%',
            opacity: 0, cursor: 'pointer', height: 24, zIndex: 1,
          }}
        />
        {/* Thumb */}
        <div style={{
          position: 'absolute',
          left: `calc(${pct}% - 8px)`,
          width: 16, height: 16, borderRadius: '50%',
          background: accentColor,
          border: `2px solid rgba(3,8,22,0.8)`,
          boxShadow: `0 0 10px ${accentColor}60`,
          pointerEvents: 'none', transition: 'left 0.05s',
        }} />
      </div>

      {description && (
        <div style={{ fontSize: 9, color: 'rgba(100,140,180,0.4)', marginTop: 2 }}>{description}</div>
      )}
    </div>
  );
}

interface NightLightControlsProps {
  onClose: () => void;
}

export function NightLightControls({ onClose }: NightLightControlsProps): JSX.Element {
  const {
    enabled, setEnabled, update, reset,
    brightness, glow, contrast, laserIntensity,
    navBrightness, workspaceBrightness, controlBrightness,
    bgOpacity, decoOpacity, minTextContrast, emergencyAlwaysFull,
  } = useNightLightStore();
  const [activeSection, setActiveSection] = useState<'main' | 'advanced'>('main');

  return (
    <div
      data-testid="night-light-controls"
      style={{
        width: 320,
        background: 'rgba(3,8,22,0.98)',
        border: '1px solid rgba(37,181,168,0.25)',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.8)',
      }}
    >
      {/* Header accent */}
      <div style={{ height: 2, background: 'linear-gradient(90deg, #25B5A8, #818cf8, transparent)' }} />

      {/* Title bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
        borderBottom: '1px solid rgba(37,181,168,0.1)',
      }}>
        <Moon size={15} style={{ color: '#5eead4' }} />
        <span style={{ fontSize: 13, fontWeight: 800, color: '#f0f4ff', flex: 1, letterSpacing: '0.03em' }}>
          Night Light Navigation
        </span>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(100,140,180,0.4)', padding: 3, borderRadius: 4 }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,172,210,0.7)')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(100,140,180,0.4)')}
        >
          <X size={13} />
        </button>
      </div>

      {/* Enable toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 16px', borderBottom: '1px solid rgba(37,181,168,0.08)',
        background: enabled ? 'rgba(37,181,168,0.05)' : 'transparent',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: enabled ? '#5eead4' : 'rgba(148,172,210,0.7)' }}>
            Theater Mode {enabled ? 'ON' : 'OFF'}
          </div>
          <div style={{ fontSize: 10, color: 'rgba(100,140,180,0.45)', marginTop: 2 }}>
            Only information glows. Everything else fades.
          </div>
        </div>
        <button
          data-testid="night-light-toggle"
          onClick={() => setEnabled(!enabled)}
          style={{
            width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
            background: enabled ? 'rgba(37,181,168,0.3)' : 'rgba(30,50,80,0.6)',
            border: `1px solid ${enabled ? 'rgba(37,181,168,0.6)' : 'rgba(37,50,80,0.5)'}`,
            position: 'relative', transition: 'all 0.2s', flexShrink: 0,
          }}
        >
          <div style={{
            position: 'absolute', top: 3, left: enabled ? 22 : 3,
            width: 16, height: 16, borderRadius: '50%',
            background: enabled ? '#5eead4' : 'rgba(100,140,180,0.4)',
            boxShadow: enabled ? '0 0 8px rgba(94,234,212,0.6)' : 'none',
            transition: 'left 0.2s, background 0.2s',
          }} />
        </button>
      </div>

      <div style={{ padding: '14px 16px' }}>
        {/* Section tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
          {(['main', 'advanced'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setActiveSection(s)}
              style={{
                padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
                background: activeSection === s ? 'rgba(37,181,168,0.15)' : 'transparent',
                border: `1px solid ${activeSection === s ? 'rgba(37,181,168,0.35)' : 'transparent'}`,
                color: activeSection === s ? '#5eead4' : 'rgba(100,140,180,0.45)',
              }}
            >
              {s === 'main' ? 'Controls' : 'Layers'}
            </button>
          ))}
        </div>

        {activeSection === 'main' && (
          <>
            <NightSlider
              label="Brightness"
              value={brightness}
              accentColor="#fbbf24"
              onChange={(v) => update({ brightness: v })}
              description="Overall ambient light level"
            />
            <NightSlider
              label="Glow"
              value={glow}
              accentColor="#5eead4"
              onChange={(v) => update({ glow: v })}
              description="Text & icon halo intensity"
            />
            <NightSlider
              label="Contrast"
              value={contrast}
              accentColor="#f0f4ff"
              safeFloor={0.6}
              onChange={(v) => update({ contrast: v })}
              description="Typography crispness — floor locks at WCAG AA"
            />
            <NightSlider
              label="Laser Intensity"
              value={laserIntensity}
              accentColor="#818cf8"
              onChange={(v) => update({ laserIntensity: v })}
              description="Panel border & tab underline glow"
            />
          </>
        )}

        {activeSection === 'advanced' && (
          <>
            <div style={{ fontSize: 9, color: 'rgba(37,181,168,0.5)', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 10, fontWeight: 700 }}>
              Adaptive Contrast Layers
            </div>
            <NightSlider label="Navigation Tabs" value={navBrightness} min={0.85} max={1} accentColor="#5eead4" safeFloor={0.85} onChange={(v) => update({ navBrightness: v })} description="90–100% — always legible" />
            <NightSlider label="Active Workspace" value={workspaceBrightness} min={0.65} max={1} accentColor="#60a5fa" safeFloor={0.65} onChange={(v) => update({ workspaceBrightness: v })} description="80–90% — your working area" />
            <NightSlider label="Secondary Controls" value={controlBrightness} min={0.4} max={1} accentColor="#a78bfa" onChange={(v) => update({ controlBrightness: v })} description="60–70% — inactive/supporting" />
            <NightSlider label="Panel Backgrounds" value={bgOpacity} min={0.1} max={0.5} accentColor="#94a3b8" onChange={(v) => update({ bgOpacity: v })} description="15–25% — dark, non-competing" />
            <NightSlider label="Decorative Art" value={decoOpacity} min={0.03} max={0.25} accentColor="#475569" onChange={(v) => update({ decoOpacity: v })} description="5–10% — atmosphere only" />
          </>
        )}

        {/* Safety rule callout */}
        <div style={{
          marginTop: 12, padding: '10px 12px', borderRadius: 8,
          background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.2)',
          display: 'flex', gap: 8, alignItems: 'flex-start',
        }}>
          <Shield size={13} style={{ color: '#f87171', flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#f87171', marginBottom: 2 }}>
              SAFETY LOCK ACTIVE
            </div>
            <div style={{ fontSize: 10, color: 'rgba(148,172,210,0.55)', lineHeight: 1.5 }}>
              SOS, emergency controls, and alerts are always at 100% brightness — regardless of Night Mode settings. Drop your phone in water and you'll still see them.
            </div>
          </div>
        </div>

        {/* Reset */}
        <button
          onClick={reset}
          style={{
            marginTop: 12, width: '100%', padding: '8px', borderRadius: 8, cursor: 'pointer',
            background: 'none', border: '1px solid rgba(30,50,80,0.5)',
            color: 'rgba(100,140,180,0.45)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,172,210,0.7)')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(100,140,180,0.45)')}
        >
          <RotateCcw size={11} /> Reset to Defaults
        </button>
      </div>
    </div>
  );
}
